/* eslint-disable react/no-unescaped-entities */
/* Keyboard-driven review mode: walk every question in a module one at a time
   with single-key hotkeys. "M" flags a question for reassignment IN PLACE
   (does not exit the flow), so a long pass never loses position.
   On exit, if any marks accumulated, show a compact reassignment sheet
   with per-row Move controls. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, ChevronsDown, Edit3, Flag, Trash2, X,
} from "lucide-react";
import { adminApi } from "../portal/shell";

const COURSE_ID = "advanced-rpas-2026";

// State→(label, color) so the metadata line in review mode matches the
// badges shown elsewhere in the authoring surface.
function stateChip(q) {
  if (q.is_placeholder) return { label: "PLACEHOLDER", color: "#f0be3c" };
  if (q.is_ai_drafted) return { label: "AI DRAFT", color: "#9c6cf8" };
  if (q.is_author_provided) return { label: "AUTHOR PENDING", color: "#6db8ff" };
  return { label: "APPROVED", color: "#3dc0af" };
}

export function ReviewMode({ module, courseModules, onClose, onChange }) {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingInline, setEditingInline] = useState(null); // qid open in reassign form
  const [showMarksSheet, setShowMarksSheet] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const qs = await adminApi.listQuestions(COURSE_ID, module.id);
        // Real content first, placeholders last so the reviewer's time is spent
        // on questions that actually matter.
        qs.sort((a, b) => Number(!!a.is_placeholder) - Number(!!b.is_placeholder));
        setQuestions(qs);
      } catch (e) { toast.error(e.message || "Load failed"); }
      finally { setLoading(false); }
    })();
    setIdx(0);
  }, [module.id]);

  const q = questions[idx] || null;
  const total = questions.length;
  const markedCount = questions.filter((x) => x.marked_for_reassign).length;

  const next = useCallback(() => {
    setIdx((i) => Math.min(i + 1, total - 1));
  }, [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  const toggleMark = useCallback(async () => {
    if (!q) return;
    const willBeMarked = !q.marked_for_reassign;
    // Optimistic update — flag flips instantly on the card, request runs in
    // background. Rollback on failure so the reviewer sees the actual state.
    setQuestions((qs) => qs.map((x, i) => i === idx ? { ...x, marked_for_reassign: willBeMarked } : x));
    setTimeout(next, 40);
    try {
      await adminApi.toggleMark(q.id, willBeMarked);
      onChange?.();  // refresh course-tree counters
    } catch (e) {
      toast.error(e.message || "Mark failed");
      setQuestions((qs) => qs.map((x) => x.id === q.id ? { ...x, marked_for_reassign: !willBeMarked } : x));
    }
  }, [q, idx, next, onChange]);

  const approve = useCallback(async () => {
    if (!q) return;
    if (!q.is_author_provided && !q.is_ai_drafted) { next(); return; }
    setBusy(true);
    try {
      await adminApi.updateQuestion(q.id, {
        prompt: q.prompt, options: q.options, correct_index: q.correct_index,
        rationale: q.rationale || "", scope: q.scope || "both",
        is_placeholder: false, is_ai_drafted: false, is_author_provided: false,
        learning_objective_ref: q.learning_objective_ref || null,
      });
      // Update in-place so the badge changes immediately
      setQuestions((qs) => qs.map((x, i) => i === idx
        ? { ...x, is_ai_drafted: false, is_author_provided: false } : x));
      toast.success(`Approved · Q${idx + 1}/${total}`);
      next();
      onChange?.();
    } catch (e) { toast.error(e.message || "Approve failed"); }
    finally { setBusy(false); }
  }, [q, idx, total, next, onChange]);

  const deleteQ = useCallback(async () => {
    if (!q) return;
    if (!window.confirm(`Delete Q${idx + 1}?  "${q.prompt.slice(0, 60)}…"`)) return;
    setBusy(true);
    try {
      await adminApi.deleteQuestion(q.id);
      setQuestions((qs) => qs.filter((_, i) => i !== idx));
      setIdx((i) => Math.min(i, questions.length - 2));  // clamp
      toast.success("Deleted");
      onChange?.();
    } catch (e) { toast.error(e.message || "Delete failed"); }
    finally { setBusy(false); }
  }, [q, idx, questions.length, onChange]);

  const exit = useCallback(() => {
    if (markedCount > 0) { setShowMarksSheet(true); return; }
    onClose();
  }, [markedCount, onClose]);

  // Global hotkey handler — disabled while editing inline or inside modal inputs
  useEffect(() => {
    const handler = (e) => {
      if (editingInline || showMarksSheet) return;
      // Ignore keys typed into inputs / textareas (the outer scope has none, but be safe)
      const tag = (e.target && e.target.tagName) || "";
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "arrowright" || k === " " || k === "k") { e.preventDefault(); next(); }
      else if (k === "arrowleft" || k === "p") { e.preventDefault(); prev(); }
      else if (k === "m") { e.preventDefault(); toggleMark(); }
      else if (k === "e") { e.preventDefault(); if (q) setEditingInline(q.id); }
      else if (k === "a") { e.preventDefault(); approve(); }
      else if (k === "d") { e.preventDefault(); deleteQ(); }
      else if (k === "escape") { e.preventDefault(); exit(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev, toggleMark, approve, deleteQ, exit, q, editingInline, showMarksSheet]);

  const chip = q ? stateChip(q) : null;
  const isMarked = q && !!q.marked_for_reassign;
  const moduleTitle = `Module ${module.code} · ${module.title}`;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(2,6,7,0.94)", zIndex: 200,
      display: "flex", flexDirection: "column", overflow: "auto",
    }} data-testid="review-mode-overlay">
      {/* Header */}
      <div style={{
        padding: "14px 24px", borderBottom: "1px solid #1c2a27",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap",
        background: "#08110f",
      }}>
        <div>
          <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
            color: "#9c6cf8", textTransform: "uppercase" }}>
            Review mode
          </div>
          <div style={{ color: "#e2ece9", font: "700 15px 'Montserrat','Geist','Inter'", marginTop: 2 }}>
            {moduleTitle}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", font: "500 12px 'JetBrains Mono', monospace" }}>
          <span style={{ color: "#7b8b87" }}>
            Q <b style={{ color: "#e2ece9" }}>{Math.min(idx + 1, total)}</b> / {total}
          </span>
          {markedCount > 0 && (
            <span style={{ color: "#c9a8ff", cursor: "pointer",
              padding: "3px 10px", border: "1px solid rgba(156,108,248,0.5)", borderRadius: 3 }}
              onClick={() => setShowMarksSheet(true)}
              data-testid="review-marks-count"
              title="Open the marks summary sheet">
              <Flag size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {markedCount} marked
            </span>
          )}
          <button className="tbg-btn ghost" style={{ padding: "5px 12px" }} onClick={exit}
            data-testid="review-exit">
            <X size={12} /> Exit
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: "#0d1817" }}>
        <div style={{
          height: 3, background: "#9c6cf8",
          width: total ? `${((idx + 1) / total) * 100}%` : 0,
          transition: "width 200ms ease-out",
        }} />
      </div>

      {/* Card */}
      <div style={{ flex: 1, display: "grid", placeItems: "start center", padding: "24px 20px" }}>
        {loading ? (
          <div style={{ color: "#7b8b87", padding: 40 }}>Loading…</div>
        ) : !q ? (
          <div style={{ color: "#7b8b87", padding: 40 }}>No questions in this module.</div>
        ) : (
          <div ref={cardRef} style={{
            maxWidth: 820, width: "100%",
            background: "#0a1413", border: `1px solid ${isMarked ? "rgba(156,108,248,0.55)" : "#1c2a27"}`,
            borderRadius: 6, padding: "22px 26px",
          }} data-testid="review-card">
            {/* Metadata line — always visible so the reviewer isn't judging blind */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
              marginBottom: 14 }}>
              <span style={{
                font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
                color: chip.color, padding: "3px 8px",
                border: `1px solid ${chip.color}55`, borderRadius: 3,
              }} data-testid="review-state-chip">
                {chip.label}
              </span>
              <span style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87" }}>
                <span style={{ color: "#a5b8b3" }}>Module:</span>{" "}
                <b style={{ color: "#e2ece9" }} data-testid="review-module">{module.code} · {module.title}</b>
              </span>
              <span style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87" }}>
                <span style={{ color: "#a5b8b3" }}>LO:</span>{" "}
                <em style={{ color: q.learning_objective_ref ? "#3dc0af" : "#ef6a6a" }}
                  data-testid="review-lo-ref">
                  {q.learning_objective_ref || "no LO tag"}
                </em>
              </span>
              {q.source_question_number != null && (
                <span style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#5a6a67" }}>
                  src #{q.source_question_number}
                </span>
              )}
              {isMarked && (
                <span style={{
                  marginLeft: "auto",
                  font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
                  color: "#c9a8ff", padding: "3px 10px",
                  border: "1px solid rgba(156,108,248,0.5)", borderRadius: 3,
                  background: "rgba(156,108,248,0.08)",
                }} data-testid="review-marked-indicator">
                  <Flag size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  MARKED FOR REASSIGN
                </span>
              )}
            </div>

            {/* Prompt */}
            <p style={{ color: "#e2ece9", font: "500 17px 'Montserrat','Geist','Inter'", lineHeight: 1.55,
              margin: "0 0 16px" }} data-testid="review-prompt">
              {q.prompt}
            </p>

            {/* Options */}
            {q.options.map((opt, i) => {
              const correct = i === q.correct_index;
              return (
                <div key={i} style={{
                  padding: "10px 14px", marginBottom: 6, borderRadius: 4,
                  border: `1px solid ${correct ? "rgba(61,192,175,0.5)" : "#1c2a27"}`,
                  background: correct ? "rgba(61,192,175,0.06)" : "transparent",
                  color: correct ? "#e2ece9" : "#a5b8b3",
                  font: `${correct ? 600 : 500} 14px 'Inter'`, lineHeight: 1.45,
                }}>
                  <strong style={{ marginRight: 8, color: correct ? "#3dc0af" : "#7b8b87" }}>
                    {String.fromCharCode(65 + i)}.
                  </strong>{opt}
                  {correct && (
                    <span style={{ marginLeft: 8, color: "#3dc0af",
                      font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em" }}>
                      ✓ CORRECT
                    </span>
                  )}
                </div>
              );
            })}

            {/* Rationale */}
            {q.rationale && (
              <div style={{ marginTop: 14, padding: "12px 14px",
                background: "#08110f", border: "1px solid #1c2a27", borderRadius: 4,
                color: "#a5b8b3", fontSize: 12.5, lineHeight: 1.6 }}>
                <strong style={{ color: "#3dc0af", letterSpacing: "0.1em", fontSize: 10,
                  textTransform: "uppercase", marginRight: 6 }}>Why:</strong>
                {q.rationale}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom action bar with hotkey hints */}
      <div style={{
        padding: "14px 24px", borderTop: "1px solid #1c2a27", background: "#08110f",
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
      }} data-testid="review-actionbar">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <HotkeyBtn hotkey="K / →" label="Keep · next" onClick={next} disabled={busy || !q} testId="review-keep" />
          <HotkeyBtn hotkey="M" label={isMarked ? "Unmark" : "Mark for reassign"}
            onClick={toggleMark} disabled={busy || !q}
            emphasis={isMarked ? "#c9a8ff" : undefined}
            testId="review-mark" icon={<Flag size={12} />} />
          <HotkeyBtn hotkey="E" label="Edit" onClick={() => q && setEditingInline(q.id)}
            disabled={busy || !q} testId="review-edit" icon={<Edit3 size={12} />} />
          <HotkeyBtn hotkey="A" label="Approve"
            onClick={approve}
            disabled={busy || !q || (!q.is_author_provided && !q.is_ai_drafted)}
            emphasis="#3dc0af" testId="review-approve" icon={<CheckCircle2 size={12} />} />
          <HotkeyBtn hotkey="D" label="Delete" onClick={deleteQ}
            disabled={busy || !q} emphasis="#ef6a6a"
            testId="review-delete" icon={<Trash2 size={12} />} />
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <HotkeyBtn hotkey="P / ←" label="Back" onClick={prev} disabled={busy || idx === 0}
            testId="review-prev" icon={<ArrowLeft size={12} />} />
          <HotkeyBtn hotkey="→" label="Next" onClick={next} disabled={busy || idx >= total - 1}
            testId="review-next" icon={<ArrowRight size={12} />} />
        </div>
      </div>

      {/* Inline reassign editor — opens without exiting review flow */}
      {editingInline && q && q.id === editingInline && (
        <InlineReassign question={q} courseModules={courseModules}
          onCancel={() => setEditingInline(null)}
          onSaved={async () => {
            setEditingInline(null);
            // Refetch the question list — server has cleared the mark on reassign
            // and may have moved the row out of this module entirely.
            try {
              const qs = await adminApi.listQuestions(COURSE_ID, module.id);
              setQuestions(qs);
              const stillHere = qs.find((x) => x.id === q.id);
              if (!stillHere) setIdx((i) => Math.min(i, qs.length - 1));
              onChange?.();
            } catch (e) { toast.error(e.message || "Reload failed"); }
          }} />
      )}

      {/* Marks summary sheet — shown on exit if any marks accumulated */}
      {showMarksSheet && (
        <MarksSheet
          questions={questions.filter((x) => x.marked_for_reassign)}
          courseModules={courseModules}
          onDone={async () => {
            setShowMarksSheet(false);
            onChange?.();
            onClose();
          }}
          onKeepReviewing={() => setShowMarksSheet(false)}
          onUnmark={async (qid) => {
            try {
              await adminApi.toggleMark(qid, false);
              setQuestions((qs) => qs.map((x) => x.id === qid ? { ...x, marked_for_reassign: false } : x));
              onChange?.();
            } catch (e) { toast.error(e.message || "Unmark failed"); }
          }}
        />
      )}
    </div>
  );
}

function HotkeyBtn({ hotkey, label, onClick, disabled, emphasis, testId, icon }) {
  const color = emphasis || "#3dc0af";
  return (
    <button className="tbg-btn ghost" onClick={onClick} disabled={disabled}
      data-testid={testId}
      style={{
        padding: "6px 12px",
        borderColor: disabled ? "#1c2a27" : `${color}66`,
        color: disabled ? "#5a6a67" : color,
        cursor: disabled ? "not-allowed" : "pointer",
      }}>
      <kbd style={{
        marginRight: 6, padding: "1px 6px",
        background: "#050807", border: "1px solid #1c2a27", borderRadius: 3,
        font: "700 9px 'JetBrains Mono', monospace", color: "#a5b8b3",
      }}>{hotkey}</kbd>
      {icon}<span style={{ marginLeft: icon ? 4 : 0 }}>{label}</span>
    </button>
  );
}

/* Inline reassign editor overlay used inside review mode. Focus goes to the
   textarea automatically. Escape / Cancel keeps position; Save advances one. */
function InlineReassign({ question, courseModules, onCancel, onSaved }) {
  const [targetModuleId, setTargetModuleId] = useState(question.module_id);
  const [loRef, setLoRef] = useState(question.learning_objective_ref || "");
  const [saving, setSaving] = useState(false);
  const dirty = targetModuleId !== question.module_id
    || (loRef || "").trim() !== (question.learning_objective_ref || "").trim();

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onCancel]);

  const save = async () => {
    if (!dirty) { onCancel(); return; }
    setSaving(true);
    try {
      await adminApi.reassignQuestion(question.id, {
        module_id: targetModuleId !== question.module_id ? targetModuleId : undefined,
        learning_objective_ref: (loRef || "").trim() === (question.learning_objective_ref || "").trim()
          ? undefined : loRef,
      });
      toast.success("Reassigned");
      onSaved();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 210,
      display: "grid", placeItems: "center", padding: 20,
    }} data-testid="review-inline-editor">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 560, width: "100%", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <strong style={{ color: "#9c6cf8", letterSpacing: "0.14em", fontSize: 11, textTransform: "uppercase" }}>
            Edit · reassign
          </strong>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onCancel}>
            <X size={12} /> Cancel
          </button>
        </div>
        <div style={{ color: "#a5b8b3", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
          {question.prompt.slice(0, 140)}{question.prompt.length > 140 ? "…" : ""}
        </div>
        <label style={{ font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
          color: "#7b8b87", textTransform: "uppercase", display: "block", marginBottom: 4 }}>
          Target module
        </label>
        <select value={targetModuleId} onChange={(e) => setTargetModuleId(e.target.value)}
          data-testid="review-inline-module-select"
          style={{
            width: "100%", background: "#050807", border: "1px solid #1c2a27",
            borderRadius: 4, padding: "6px 10px", color: "#e2ece9", marginBottom: 10,
            font: "500 12px 'JetBrains Mono', monospace",
          }}>
          {courseModules.map((m) => (
            <option key={m.id} value={m.id}>
              {m.code} · {m.title}{m.id === question.module_id ? " (current)" : ""}
            </option>
          ))}
        </select>
        <label style={{ font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
          color: "#7b8b87", textTransform: "uppercase", display: "block", marginBottom: 4 }}>
          Learning objective reference
        </label>
        <textarea value={loRef} onChange={(e) => setLoRef(e.target.value)}
          autoFocus
          data-testid="review-inline-lo-textarea"
          placeholder="e.g. Explain the effect of bank angle on turn radius (CARs 901.62)"
          style={{
            width: "100%", minHeight: 60, background: "#050807",
            border: "1px solid #1c2a27", borderRadius: 4, padding: "6px 10px",
            color: "#e2ece9", font: "500 11px 'JetBrains Mono', monospace",
          }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="tbg-btn ghost" onClick={onCancel}>Cancel</button>
          <button className="tbg-btn" onClick={save} disabled={saving || !dirty}
            data-testid="review-inline-save"
            style={{ background: "#9c6cf8" }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Marks summary sheet — the on-exit reassignment sheet with per-row Move
   controls. Uses the same reassign endpoint used everywhere else. */
function MarksSheet({ questions, courseModules, onDone, onKeepReviewing, onUnmark }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 220,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="review-marks-sheet">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 820, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#c9a8ff" }}>
              <Flag size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Marks summary
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              You marked {questions.length} question{questions.length === 1 ? "" : "s"} for reassignment
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 4, lineHeight: 1.55 }}>
              Reassign each one now — new module and/or new LO tag — or dismiss.
              Keep reviewing to add more marks.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }}
            onClick={onKeepReviewing} data-testid="review-marks-back">
            <ChevronsDown size={12} /> Keep reviewing
          </button>
        </div>

        {questions.length === 0 && (
          <div style={{ color: "#7b8b87", padding: 20, textAlign: "center" }}>
            No marks left.
          </div>
        )}

        {questions.map((q) => (
          <MarkRow key={q.id} question={q} courseModules={courseModules}
            onUnmark={() => onUnmark(q.id)} />
        ))}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button className="tbg-btn" onClick={onDone} data-testid="review-marks-done"
            style={{ background: "#3dc0af" }}>
            <CheckCircle2 size={13} /> Done · Exit review
          </button>
        </div>
      </div>
    </div>
  );
}

function MarkRow({ question, courseModules, onUnmark }) {
  const [open, setOpen] = useState(true);
  const [targetModuleId, setTargetModuleId] = useState(question.module_id);
  const [loRef, setLoRef] = useState(question.learning_objective_ref || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = targetModuleId !== question.module_id
    || (loRef || "").trim() !== (question.learning_objective_ref || "").trim();
  const state = stateChip(question);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await adminApi.reassignQuestion(question.id, {
        module_id: targetModuleId !== question.module_id ? targetModuleId : undefined,
        learning_objective_ref: (loRef || "").trim() === (question.learning_objective_ref || "").trim()
          ? undefined : loRef,
      });
      setSaved(true);
      setOpen(false);
      toast.success("Reassigned");
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      padding: "12px 14px", marginBottom: 8,
      background: "#0d1817", border: `1px solid ${saved ? "rgba(61,192,175,0.5)" : "#1c2a27"}`,
      borderRadius: 4,
    }} data-testid={`marks-row-${question.id}`}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{
          font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
          color: state.color, padding: "2px 6px",
          border: `1px solid ${state.color}55`, borderRadius: 2,
        }}>{state.label}</span>
        {saved && (
          <span style={{ color: "#3dc0af", font: "700 9px 'JetBrains Mono', monospace",
            letterSpacing: "0.16em" }}>✓ REASSIGNED</span>
        )}
        <span style={{ marginLeft: "auto", color: "#7b8b87", fontSize: 11 }}>
          <button className="tbg-btn ghost" style={{ padding: "3px 8px" }} onClick={onUnmark}
            data-testid={`marks-unmark-${question.id}`}>
            Unmark
          </button>
        </span>
      </div>
      <div style={{ color: "#e2ece9", fontSize: 12.5, lineHeight: 1.5 }}>
        {question.prompt.slice(0, 160)}{question.prompt.length > 160 ? "…" : ""}
      </div>
      {open && (
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
              color: "#7b8b87", textTransform: "uppercase", display: "block", marginBottom: 4 }}>
              Target module
            </label>
            <select value={targetModuleId} onChange={(e) => setTargetModuleId(e.target.value)}
              data-testid={`marks-module-${question.id}`}
              style={{
                width: "100%", background: "#050807", border: "1px solid #1c2a27",
                borderRadius: 4, padding: "6px 10px", color: "#e2ece9",
                font: "500 11px 'JetBrains Mono', monospace",
              }}>
              {courseModules.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} · {m.title}{m.id === question.module_id ? " (current)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
              color: "#7b8b87", textTransform: "uppercase", display: "block", marginBottom: 4 }}>
              Learning objective
            </label>
            <input value={loRef} onChange={(e) => setLoRef(e.target.value)}
              data-testid={`marks-lo-${question.id}`}
              style={{
                width: "100%", background: "#050807", border: "1px solid #1c2a27",
                borderRadius: 4, padding: "6px 10px", color: "#e2ece9",
                font: "500 11px 'JetBrains Mono', monospace",
              }} />
          </div>
          <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 6 }}>
            <button className="tbg-btn" onClick={save} disabled={saving || !dirty || saved}
              data-testid={`marks-save-${question.id}`}
              style={{ background: "#9c6cf8", padding: "5px 12px" }}>
              {saving ? "Saving…" : saved ? "Saved" : "Save reassignment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
